/* Custom percent stats — count up numbers when the section scrolls into
   view. Uses IntersectionObserver to trigger once at ~20% visibility,
   then requestAnimationFrame with an ease-out cubic curve so the numbers
   move quickly at the start and settle smoothly at the target. Honours
   prefers-reduced-motion (jumps straight to final values). Decimals
   in the source value are preserved via toFixed at each frame. */
(function () {
  if (customElements.get('custom-percent-stats')) return;

  const REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CustomPercentStats extends HTMLElement {
    connectedCallback() {
      this.counters = Array.from(this.querySelectorAll('[data-counter]'));
      if (!this.counters.length) return;

      this.duration = parseInt(this.dataset.duration, 10) || 1500;
      this.shouldAnimate = this.dataset.animate === 'true' && !REDUCED_MOTION;
      this.animated = false;

      if (!this.shouldAnimate || !('IntersectionObserver' in window)) {
        this._setFinal();
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.animated) {
              this.animated = true;
              this._animate();
              obs.disconnect();
            }
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(this);
    }

    /* Detect decimal precision in the source value (e.g. "13.6" → 1
       decimal place, "93" → 0). Then animate from 0 → target with an
       ease-out cubic so the count finishes with a soft settle rather
       than a hard cutoff. */
    _animate() {
      const start = performance.now();
      const targets = this.counters.map((c) => parseFloat(c.dataset.target) || 0);
      const decimals = this.counters.map((c) => {
        const raw = String(c.dataset.target || '0');
        const dot = raw.indexOf('.');
        return dot >= 0 ? raw.length - dot - 1 : 0;
      });

      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / this.duration);
        const eased = 1 - Math.pow(1 - progress, 3);

        this.counters.forEach((c, i) => {
          const value = targets[i] * eased;
          c.textContent = decimals[i] > 0 ? value.toFixed(decimals[i]) : Math.round(value);
        });

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          this._setFinal();
        }
      };
      requestAnimationFrame(step);
    }

    _setFinal() {
      this.counters.forEach((c) => {
        c.textContent = c.dataset.target || '0';
      });
    }
  }

  customElements.define('custom-percent-stats', CustomPercentStats);
})();
